import React, {useState, useCallback, useMemo, useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';
import _ from 'lodash';
import p5 from 'p5';
import {Box, Container, Divider, Stack, Typography,
    useMediaQuery, useTheme} from '@mui/material';
import api from '../util/api.js';
import {useHotkey} from '../util/react_hooks.js';
import {imagekitStaticURL} from '../util/util.js';
import {SceneDirector} from '../util/p5_util.js';
import {useLoaderState} from './Loader.js';
import HtmlContent from './HtmlContent.js';
import SCTextField from './SCTextField.js';
import SCButton from './SCButton.js';

const QuestionPage = props=>{
    let {question, setQuestion, setPage} = props;
    let theme = useTheme();
    let md = useMediaQuery(theme.breakpoints.down('md'));
    let sm = useMediaQuery(theme.breakpoints.down('sm'));
    let [error, setError] = useState();
    let onClick = useCallback(()=>{
        setError();
        if (!question?.trim().length)
            return void setError('נא להזין נושא');
        setPage(PAGES.SPREAD);
    }, [question]);
    useHotkey('Enter', onClick);
    return <Container maxWidth="lg">
      <Stack direction="column" gap={2} alignItems="center"
        marginTop={5}>
        <Typography variant="h6">
          באיזה נושא תרצו לקבל את המסר?
        </Typography>
        <SCTextField value={question} variant="outlined" onChange={setQuestion}
          error={!!error} helperText={error}
          sx={{minWidth: sm ? '200px' : md ? '400px' : '600px'}}/>
        <SCButton text="לבחירת קלף מסר" variant="outlined" onClick={onClick} size="large"/>
      </Stack>
    </Container>;
};

const CARD_HEIGHT = 727;
const CARD_WIDTH = 428;
const NUM_CARDS = 78;

class LoadingScene { constructor(){} }

class ShuffleScene {
    static STATES = {
        IDLE: Symbol('idle'),
        SHUFFLING: Symbol('shuffling'),
        END: Symbol('end'),
    };
    constructor(shuffleLayer, ctx){
        this.shuffleLayer = shuffleLayer;
        this.ctx = ctx;
        this.state = ShuffleScene.STATES.IDLE;
        this.shuffleTime = 0;
        this.zoomTime = 0;
        this.leftCardPos = 0;
        this.shufflePos = 0;
    }
    draw(p, sceneDirector){
        if (this.state===ShuffleScene.STATES.SHUFFLING) {
            this.shuffleTime+=p.deltaTime;
            this.zoomTime = p.constrain(this.zoomTime+p.deltaTime, 0, 500);
            this.leftCardPos = Math.max(-this.shuffleTime, -150);
            this.shufflePos = Math.sin(this.shuffleTime*0.005)*220;
        }
        else if (this.state===ShuffleScene.STATES.END) {
            this.leftCardPos = Math.min(0, this.leftCardPos+0.5*p.deltaTime);
            this.shufflePos = Math.max(0, this.shufflePos-0.5*p.deltaTime);
            this.zoomTime = Math.max(0, this.zoomTime-0.5*p.deltaTime);
        }
        this.shuffleLayer.clear();
        if (this.state===ShuffleScene.STATES.IDLE) {
            let transX = this.shuffleLayer.width/2;
            let transY = this.shuffleLayer.height/2;
            this.shuffleLayer.push();
            this.shuffleLayer.translate(transX, transY);
            this.shuffleLayer.image(this.ctx.cardBack, 0, 0,
                CARD_WIDTH*this.ctx.cardScale, CARD_HEIGHT*this.ctx.cardScale);
            this.shuffleLayer.pop();
        } else {
            for (let i = 0; i<5; i++) {
                let spos = this.shufflePos;
                if (this.state==ShuffleScene.STATES.SHUFFLING)
                    spos-=(i*i*20);
                spos = p.constrain(spos, -150, 150);
                this.shuffleLayer.push();
                this.shuffleLayer.translate(this.shuffleLayer.width/2+spos, this.shuffleLayer.height/2);
                this.shuffleLayer.image(this.ctx.cardBack, 0, 0, CARD_WIDTH*this.ctx.cardScale, CARD_HEIGHT*this.ctx.cardScale);
                this.shuffleLayer.pop();
            }
            this.shuffleLayer.push();
            this.shuffleLayer.translate(this.shuffleLayer.width/2+this.leftCardPos, this.shuffleLayer.height/2);
            this.shuffleLayer.image(this.ctx.cardBack, 0, 0, CARD_WIDTH*this.ctx.cardScale, CARD_HEIGHT*this.ctx.cardScale);
            this.shuffleLayer.pop();
        }
        p.clear();
        p.push();
        p.translate(p.width/2, p.height/2)
        p.scale(p.lerp(1, 1.2, p.constrain(this.zoomTime*0.0005, 0, 1)));
        p.rotate(p.lerp(0, -50, p.constrain(this.zoomTime*0.0005, 0, 1)));
        p.image(this.shuffleLayer, 0, 0);
        p.pop();
        if (this.state==ShuffleScene.STATES.IDLE) {
            p.push();
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(24);
            p.text(`לחצו לערבוב`, p.width/2, 20);
            p.pop();
        } else if (this.state==ShuffleScene.STATES.SHUFFLING) {
            p.push();
            p.textAlign(p.CENTER, p.CENTER);
            p.textSize(24);
            p.text(`לחצו להפסקת ערבוב`, p.width/2, 20);
            p.pop();
        }
        if (this.state==ShuffleScene.STATES.END&&!this.shufflePos&&!this.zoomTime&&!this.leftCardPos)
            sceneDirector.setScene(new SpreadScene(this.ctx));
    }
    mousePressed(p){
        switch (this.state) {
            case ShuffleScene.STATES.IDLE:
                this.state = ShuffleScene.STATES.SHUFFLING;
                break;
            case ShuffleScene.STATES.SHUFFLING:
                this.state = ShuffleScene.STATES.END;
                break;
        }
    }
}

class SpreadScene {
    constructor(ctx){
        this.ctx = ctx;
        this.selectedCard = -1;
        this.spreading = true;
        this.spreadTime = 1000;
        this.spreadDuration = 0;
    }
    draw(p){
        this.spreadDuration+=p.deltaTime;
        if (this.spreadDuration>this.spreadTime)
            this.spreading = false;
        this.selectedCard = -1;
        let transX = p.width/2;
        let transY = p.height/2+CARD_HEIGHT*this.ctx.cardScale*0.8-30;
        // first run to determine mouse position
        if (!this.spreading) {
            for (let i = 0; i<NUM_CARDS; i++) {
                let rotate = -this.ctx.spreadAngle/2+(i/NUM_CARDS*this.ctx.spreadAngle);
                let m = p.createVector(p.mouseX, p.mouseY);
                m.sub(transX, transY);
                m.rotate(-rotate);
                let rectX = 0;
                let rectY = -CARD_HEIGHT*this.ctx.cardScale*0.8;
                let rectW = CARD_WIDTH*this.ctx.cardScale;
                let rectH = CARD_HEIGHT*this.ctx.cardScale;
                if (
                    m.x>=rectX-rectW/2&&
                    m.y>=rectY-rectH/2&&
                    m.x<rectX+rectW/2&&
                    m.y<rectY+rectH/2
                ) {
                    this.selectedCard = i;
                }
            }
        }
        // second run to actually draw
        for (let i = 0; i<NUM_CARDS; i++) {
            p.push();
            let rotate = -this.ctx.spreadAngle/2+(i/NUM_CARDS*this.ctx.spreadAngle);
            if (this.spreading)
                rotate*=Math.sin(((this.spreadDuration/this.spreadTime)*Math.PI)/2); // ease out
            p.translate(transX, transY);
            p.rotate(rotate);
            let rectX = 0;
            let rectY = -CARD_HEIGHT*this.ctx.cardScale*0.8;
            let rectW = CARD_WIDTH*this.ctx.cardScale;
            let rectH = CARD_HEIGHT*this.ctx.cardScale;
            let offsetY = i==this.selectedCard ? -50 : 0;
            p.image(this.ctx.cardBack, rectX, rectY+offsetY, rectW, rectH);
            if (i==this.selectedCard) {
                p.push();
                p.noFill();
                p.strokeWeight(4);
                p.stroke(p.color(170, 170, 250));
                p.rect(rectX, rectY+offsetY, rectW, rectH, 20);
                p.pop();
            }
            p.pop();
        }
    }
    mousePressed(p){
        if (this.selectedCard!=-1)
            this.ctx.onSelect(this.selectedCard);
    }
}

const runP5 = (element, onSelect)=>{
    let ctx = {
        cardScale: 0.4,
        spreadAngle: 120,
        cardBack: null,
        onSelect,
    };
    let coordsComputed;
    let shuffleLayer;
    let sceneDirector;
    let setup = p=>{
        p.createCanvas(element.offsetWidth, element.offsetHeight)
            .parent(element);
        shuffleLayer = p.createGraphics(element.offsetWidth, element.offsetHeight);
        shuffleLayer.imageMode(p.CENTER);
        shuffleLayer.angleMode(p.DEGREES);
        shuffleLayer.rectMode(p.CENTER);
        p.loadImage(imagekitStaticURL`tarotmail-assets/tarot-back.png`, img=>{
            ctx.cardBack = img;
            sceneDirector.setScene(new ShuffleScene(shuffleLayer, ctx));
        });
        p.imageMode(p.CENTER);
        p.angleMode(p.DEGREES);
        p.rectMode(p.CENTER);
        sceneDirector = new SceneDirector(p, new LoadingScene());
    };
    let computeCoords = p=>{
        if (element.offsetWidth>1000) {
            ctx.cardScale = 0.5;
            ctx.spreadAngle = 120;
        } else if (element.offsetWidth>800) {
            ctx.cardScale = 0.5;
            ctx.spreadAngle = 100;
        } else if (element.offsetHeight>600) {
            ctx.cardScale = 0.5;
            ctx.spreadAngle = 90;
        } else {
            ctx.cardScale = 0.3;
            ctx.spreadAngle = 60;
        }
        coordsComputed = true;
    };
    let mousePressed = p=>{
        sceneDirector.mousePressed();
    };
    let draw = p=>{
        if (!coordsComputed)
            computeCoords(p);
        p.clear();
        sceneDirector.draw();
    };
    let windowResized = p=>{
        p.resizeCanvas(element.offsetWidth, element.offsetHeight);
        coordsComputed = false;
    };
    let p5Instance = new p5(p=>{
        p.setup = ()=>setup(p);
        p.draw = ()=>draw(p);
        p.mousePressed = ()=>mousePressed(p);
        p.windowResized = ()=>windowResized(p);
    });
    return ()=>p5Instance.remove();
};

const SpreadPage = props=>{
    let {question, setPage} = props;
    let [p5Node, setP5Node] = useState();
    useEffect(()=>{
        if (!p5Node)
            return;
        return runP5(p5Node, ()=>setPage(PAGES.ANSWER));
    }, [p5Node]);
    return <Stack direction="column" gap={3} alignItems="center"
      marginTop={5}
      sx={{height: '600px'}}>
      <Typography variant="h5">
        {question}
      </Typography>
      <div ref={setP5Node} style={{
          width: '100%',
          flexGrow: 1,
      }}>
      </div>
    </Stack>;
};

const AnswerPage = props=>{
    let {question, setQuestion, setPage, CtaComp} = props;
    let onClick = useCallback(()=>{
        setQuestion('');
        setPage(PAGES.QUESTION);
    }, []);
    let {data: card, isLoading} = useQuery({
        queryKey: ['random_card'],
        queryFn: api.randomCard,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        cacheTime: 0,
        gcTime: 0,
    });
    useLoaderState(isLoading);
    let theme = useTheme();
    let md = useMediaQuery(theme.breakpoints.down('md'));
    if (!card)
        return null;
    return <Stack direction="column" gap={3}
      alignItems="center"
      marginTop={5} marginBottom={5}>
      <Typography variant="h5">
        מסר בנושא: "{question}"
      </Typography>
      <Divider sx={{width: '100%'}}/>
      <Typography variant="h3">
        {card.title}
      </Typography>
      <Typography variant="h6">
        {card.subtitle}
      </Typography>
      <Stack direction={md ? 'column' : 'row'} gap={3}>
        <Box component="img" src={card.image}
          sx={{objectFit: 'contain', maxHeight: '500px'}}/>
        <Stack direction="column" gap={3} alignItems="start">
            <HtmlContent content={card.content}/>
            {CtaComp&&<CtaComp question={question} card={card}/>}
        </Stack>
      </Stack>
      <SCButton text="למסר נוסף" variant="outlined" onClick={onClick} size="large"/>
    </Stack>;
};

const PAGES = {
    QUESTION: 'question',
    SPREAD: 'spread',
    ANSWER: 'answer',
};

const PAGE_COMPS = {
    [PAGES.QUESTION]: QuestionPage,
    [PAGES.SPREAD]: SpreadPage,
    [PAGES.ANSWER]: AnswerPage,
};

const TarotSpreadComp = props=>{
    let {CtaComp} = props;
    let [question, setQuestion] = useState('');
    let [page, setPage] = useState(PAGES.QUESTION);
    let Comp = useMemo(()=>PAGE_COMPS[page], [page]);
    return <Comp
      question={question}
      setQuestion={setQuestion}
      setPage={setPage}
      CtaComp={CtaComp}
    />;
};

export default TarotSpreadComp;
